'use client'

import React, { createContext, useContext } from 'react'

import { Box, HTMLChakraProps, Text } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import {
  sectionContentStyles,
  sectionHeaderStyles,
  sectionMetaRowStyles,
  sectionMetadataStyles,
  sectionStyles,
  steppedListRootStyles,
} from './SteppedList.styles'

const SteppedListContext = createContext<ColorScheme>('dark')

export type SteppedListMetadata = {
  name: string
  completedAt: string
}

export type SteppedListProps = HTMLChakraProps<'div'> & {
  colorScheme?: ColorScheme
  children: React.ReactNode
}

export type SteppedListSectionProps = Omit<
  HTMLChakraProps<'div'>,
  'onSelect'
> & {
  colorScheme?: ColorScheme
  header: string
  content: string
  metadata?: SteppedListMetadata
}

export const SteppedList = ({
  colorScheme = 'dark',
  children,
  ...rest
}: SteppedListProps) => (
  <SteppedListContext.Provider value={colorScheme}>
    <Box {...steppedListRootStyles} {...rest}>
      {children}
    </Box>
  </SteppedListContext.Provider>
)

export const SteppedListSection = ({
  colorScheme,
  header,
  content,
  metadata,
  ...rest
}: SteppedListSectionProps) => {
  const contextColorScheme = useContext(SteppedListContext)
  const resolvedColorScheme = colorScheme ?? contextColorScheme

  return (
    <Box {...sectionStyles(resolvedColorScheme)} {...rest}>
      <Box {...sectionMetaRowStyles}>
        <Text {...sectionHeaderStyles(resolvedColorScheme)}>{header}</Text>
        {metadata && (
          <Text {...sectionMetadataStyles(resolvedColorScheme)}>
            {metadata.name
              ? `${metadata.name} - ${metadata.completedAt}`
              : metadata.completedAt}
          </Text>
        )}
      </Box>
      <Text {...sectionContentStyles(resolvedColorScheme)}>{content}</Text>
    </Box>
  )
}
